import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type AttachmentMediaRadiusConstantType,
    type AttachmentColorsConstantType,
    type AttachmentRadiusConstantType,
    type AttachmentDynamicStyleType,
    type AttachmentSizeConstantType,
    type AttachmentMeasures,
    type AttachmentColors,
    type AttachmentSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const ATTACHMENT_TITLE_VARIANTS: Record<AttachmentSize, keyof NCoreUIKit.Typography> = {
    medium: "labelMediumSize",
    large: "labelLargeSize",
    small: "labelSmallSize"
};

export const ATTACHMENT_DESCRIPTION_VARIANTS: Record<AttachmentSize, keyof NCoreUIKit.Typography> = {
    large: "bodyMediumSize",
    medium: "bodySmallSize",
    small: "bodySmallSize"
};

export const ATTACHMENT_MEDIA_ICON_SIZES: Record<AttachmentSize, keyof NCoreUIKit.IconSizes> = {
    medium: "medium",
    large: "large",
    small: "medium"
};

export const ATTACHMENT_DESCRIPTION_ICON_OFFSETS: Record<AttachmentSize, boolean> = {
    large: false,
    medium: true,
    small: true
};

export const getAttachmentSize = ({
    controlHeights,
    shapeConfigs,
    iconSizes,
    size
}: AttachmentSizeConstantType): AttachmentMeasures => {
    return {
        descriptionIconSize: ATTACHMENT_DESCRIPTION_ICON_OFFSETS[size] ? iconSizes.small - shapeConfigs.chipIconOffset : iconSizes.small,
        mediaIconSize: iconSizes[ATTACHMENT_MEDIA_ICON_SIZES[size]],
        descriptionVariant: ATTACHMENT_DESCRIPTION_VARIANTS[size],
        titleVariant: ATTACHMENT_TITLE_VARIANTS[size],
        mediaSize: controlHeights[size]
    };
};

export const getAttachmentColors = ({
    uploadState,
    colors
}: AttachmentColorsConstantType): AttachmentColors => {
    const isError = uploadState === "error";

    const isProgress = uploadState === "uploading" || uploadState === "processing";

    const getMediaColor = () => {
        return isProgress ? "transparent" : colors.content.container.subtle;
    };

    const getBorderColor = () => {
        if(isError) {
            return colors.content.border.danger;
        }

        return uploadState === "idle" ? colors.content.border.default : colors.content.border.subtle;
    };

    return {
        descriptionColor: isError ? colors.content.text.danger : colors.content.text.mid,
        borderStyle: uploadState === "idle" ? "dashed" : "solid",
        containerColor: colors.content.container.raised,
        mediaIconColor: colors.content.icon.mid,
        titleColor: colors.content.text.high,
        borderColor: getBorderColor(),
        mediaColor: getMediaColor()
    };
};

export const getAttachmentRadius = ({
    controlPaddings,
    isInField,
    radiuses,
    borders,
    spaces
}: AttachmentRadiusConstantType): number => {
    if(!isInField) {
        return radiuses.surface;
    }

    return concentric({
        inset: Math.max(spaces.spacingSm, controlPaddings.small) + borders.line,
        role: radiuses.surface,
        outer: radiuses.form
    });
};

export const getAttachmentMediaRadius = ({
    borderRadius,
    radiuses,
    borders,
    spaces
}: AttachmentMediaRadiusConstantType): number => {
    return concentric({
        inset: spaces.spacingSm + borders.line,
        outer: borderRadius,
        role: radiuses.form
    });
};

const stylesheet = StyleSheet.create({
    vertical: {
        flexDirection: "column",
        flexShrink: 0
    },
    container: {
        borderStyle: "solid",
        maxWidth: "100%",
        minWidth: 0
    },
    horizontal: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 0
    },
    footer: {
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    }
});

export const useStyles = ({
    controlPaddings,
    controlHeights,
    currentColors,
    borderRadius,
    shapeConfigs,
    currentSize,
    orientation,
    shadowAlpha,
    uploadState,
    elevations,
    borders,
    colors,
    spaces
}: AttachmentDynamicStyleType) => {
    const styles = {
        container: {
            backgroundColor: currentColors.containerColor,
            borderStyle: currentColors.borderStyle,
            borderColor: currentColors.borderColor,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            }),
            borderWidth: borders.line,
            padding: spaces.spacingSm
        } as Mutable<ViewStyle>,
        footer: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>
    };

    if(orientation === "horizontal") {
        Object.assign(styles.container, {
            minHeight: currentSize.mediaSize + spaces.spacingSm * 2 + borders.line * 2,
            paddingEnd: controlPaddings.small,
            width: shapeConfigs.sideListWidth,
            gap: spaces.spacingSm
        });
    }

    if(orientation === "vertical") {
        Object.assign(styles.container, {
            width: controlHeights.large * 3,
            gap: spaces.spacingSm
        });
    }

    if(uploadState !== "idle") {
        Object.assign(styles.container, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    return styles;
};
export default stylesheet;
